import {
  PropsWithoutRef,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import {
  GetMessageWithIntl,
  MessageProps,
  ModalDialogComponent,
  ModalDialogComponentProps,
  error,
} from '~/shared/components';
import { CaptionButton } from '~/shared/components/ui/Button';
import {
  CategorizedCheckList,
  CategorizedList,
  CheckItem,
} from '~/shared/components/ui/CategorizedCheckList';
import { ConditionDetail, MultipleType } from '../../util';
import './ConditionTreeDialog.css';

export interface MaterialTreeDialogRef {
  open: (
    listData: CategorizedList,
    condition?: ConditionDetail,
    focusId?: string
  ) => void;
}
interface MaterialTreeDialogProps {
  messageOption: {
    headerLabelId: MessageProps;
    labelEveryMatch?: MessageProps;
    labelSomeMatch?: MessageProps;
  };
  onDecision?: (condition: ConditionDetail) => void;
  onCancel?: () => void;
  onCheckCount?: (condition: ConditionDetail) => Promise<Long>;
}

export const MaterialTreeDialog = forwardRef(
  (props: PropsWithoutRef<MaterialTreeDialogProps>, ref) => {
    const intl = useIntl();
    const [isOpen, setOpen] = useState(false);
    const [listData, setListData] = useState<CategorizedList>([]);
    const [initCheckIds, setInitCheckIds] = useState<string[]>([]);
    const [multipleType, setMultipleType] = useState<MultipleType>('or');
    const [checkedIds, setCheckedIds] = useState<CheckItem[]>([]);
    const [focusId, setFocusId] = useState<string>();

    const handleChangeChecks = useCallback((checkIds: CheckItem[]) => {
      setCheckedIds(checkIds);
    }, []);

    const handleResetCondition = () => {
      setInitCheckIds([]);
    };

    const handleCancel = () => {
      setOpen(false);
    };

    const handleDecision = () => {
      // if (checkedIds.length === 0) {
      //   error([GetMessageWithIntl(intl, { id: 'E0000023' })]);
      //   return;
      // }
      if (props.onDecision) {
        props.onDecision({
          multipleType,
          conditionItems: checkedIds.map((item) => ({
            item,
            checked: true,
          })),
        });
      }
      setOpen(false);
    };

    useImperativeHandle(
      ref,
      (): MaterialTreeDialogRef => ({
        open: (
          listData: CategorizedList,
          condition?: ConditionDetail,
          focusId?: string
        ) => {
          setListData(listData);
          if (condition) {
            setMultipleType(condition.multipleType);
            setInitCheckIds(
              condition.conditionItems
                .filter((condition) => condition.checked)
                .map((condition) => condition.item.id || '')
            );
          } else {
            setMultipleType('or');
            setInitCheckIds([]);
          }
          if (focusId) {
            setFocusId(focusId);
          }
          setOpen(true);
        },
      })
    );

    const elements = (
      <div className="ConditionTreeDialog">
        <div>
          <CategorizedCheckList
            listData={listData}
            checkedIds={initCheckIds}
            onChange={handleChangeChecks}
            focusId={focusId}
          />
        </div>
        <div className="button-area">
          <CaptionButton
            name="reset"
            className="reset-condition-button"
            caption="条件解除"
            onClick={handleResetCondition}
          />
          <CaptionButton
            name="save"
            buttonType="basic"
            className="button"
            caption={GetMessageWithIntl(intl, { id: 'select' })}
            onClick={() => handleDecision()}
          />
        </div>
      </div>
    );

    const openModalProps: ModalDialogComponentProps = {
      cancel: handleCancel,
      send: () => {},
      modalIsOpen: isOpen,
      headerLabelId: props.messageOption.headerLabelId,
      messageLabelId: {},
      elements,
      controls: { showCloseHeaderButton: true },
    };

    return (
      <div className="MaterialTreeDialogBase">
        <ModalDialogComponent {...openModalProps} />
      </div>
    );
  }
);
